import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink } from 'react-icons/fi';
import { AirplaneTakeoff, House } from '@phosphor-icons/react';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { formatLocalDateTime, parseDateInput, toLocalDateKey } from '../../utils/formatters';

const ESPN_LOGO_URL = 'https://a.espncdn.com/i/teamlogos/nba/500';

// Team abbreviation mapping for ESPN logos
const TEAM_ABBR_MAP = {
  'Philadelphia 76ers': 'phi',
  '76ers': 'phi',
  'Dallas Mavericks': 'dal',
  'Mavericks': 'dal',
  'Memphis Grizzlies': 'mem',
  'Grizzlies': 'mem',
  'Los Angeles Lakers': 'lal',
  'Lakers': 'lal',
  'Boston Celtics': 'bos',
  'Celtics': 'bos',
  'Golden State Warriors': 'gsw',
  'Warriors': 'gsw',
  'Miami Heat': 'mia',
  'Heat': 'mia',
  'Phoenix Suns': 'phx',
  'Suns': 'phx',
  'Denver Nuggets': 'den',
  'Nuggets': 'den',
  'Milwaukee Bucks': 'mil',
  'Bucks': 'mil',
  'Cleveland Cavaliers': 'cle',
  'Cavaliers': 'cle',
  'Oklahoma City Thunder': 'okc',
  'Thunder': 'okc',
  'New York Knicks': 'ny',
  'Knicks': 'ny',
  'Brooklyn Nets': 'bkn',
  'Nets': 'bkn',
  'Toronto Raptors': 'tor',
  'Raptors': 'tor',
  'Chicago Bulls': 'chi',
  'Bulls': 'chi',
  'Indiana Pacers': 'ind',
  'Pacers': 'ind',
  'Detroit Pistons': 'det',
  'Pistons': 'det',
  'Atlanta Hawks': 'atl',
  'Hawks': 'atl',
  'Charlotte Hornets': 'cha',
  'Hornets': 'cha',
  'Washington Wizards': 'wsh',
  'Wizards': 'wsh',
  'Orlando Magic': 'orl',
  'Magic': 'orl',
  'San Antonio Spurs': 'sa',
  'Spurs': 'sa',
  'Houston Rockets': 'hou',
  'Rockets': 'hou',
  'New Orleans Pelicans': 'no',
  'Pelicans': 'no',
  'Minnesota Timberwolves': 'min',
  'Timberwolves': 'min',
  'Utah Jazz': 'utah',
  'Jazz': 'utah',
  'Portland Trail Blazers': 'por',
  'Trail Blazers': 'por',
  'Sacramento Kings': 'sac',
  'Kings': 'sac',
  'Los Angeles Clippers': 'lac',
  'Clippers': 'lac',
};

export function MatchHeader({ market, prediction, teamStats }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!market) return null;

  const { teamA, teamB, priceA, priceB, status, gameTime, polymarketUrl, slug } = market;

  const getTeamLogo = (team) => {
    if (!team) return '';
    const abbr = TEAM_ABBR_MAP[team] || team.toLowerCase().split(' ').pop();
    return `${ESPN_LOGO_URL}/${abbr}.png`;
  };

  // Calculate rest days from team stats
  // Rest days = days since last game (from today) + 1 (inclusive counting)
  // This matches the original website's calculation
  // Example: last game 12/30, today 1/1 = 2 days diff + 1 = 3 days rest
  const calculateRestDays = (stats) => {
    if (!stats?.recentGames?.length) return null;
    
    // Get the most recent game date (format: "YYYY-MM-DD")
    const lastGameDateStr = stats.recentGames[0].date;
    const lastDateKey = toLocalDateKey(lastGameDateStr);
    const todayKey = toLocalDateKey(new Date());
    if (!lastDateKey || !todayKey) return null;

    const lastDate = parseDateInput(lastDateKey);
    const currDate = parseDateInput(todayKey);
    if (!lastDate || !currDate) return null;

    const diffTime = currDate.getTime() - lastDate.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return diffDays > 0 ? diffDays : null;
  };

  const teamARestDays = calculateRestDays(teamStats?.teamA);
  const teamBRestDays = calculateRestDays(teamStats?.teamB);

  return (
    <Card className="p-6">
      {/* Back button and slug */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <FiArrowLeft />
          {t('common.backToHome')}
        </button>
        <span className="text-sm text-gray-500 font-mono">{slug}</span>
      </div>

      {/* Main match display */}
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        {/* Team A (Away) */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 text-sm text-accent">
            <AirplaneTakeoff size={18} weight="duotone" />
            <span>{t('match.away')}</span>
          </div>
          <h1 className="text-lg sm:text-xl font-bold mb-2">{teamA}</h1>
          <div className="text-sm text-gray-400 mb-3">
            {t('match.rest')}: <span className="text-white">{teamARestDays ?? '-'} {t('match.days')}</span>
          </div>
          <img
            src={getTeamLogo(teamA)}
            alt={teamA}
            className="w-16 h-16 sm:w-24 sm:h-24 object-contain"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
        </div>

        {/* Center - Odds Display */}
        <div className="flex-1 text-center px-4">
          <div className="text-sm text-gray-400 mb-3">{t('match.currentOdds')}</div>
          <div className="flex items-center justify-center gap-6">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white">
                {Math.round((priceA || 0) * 100)}
                <span className="text-xl">%</span>
              </div>
              <div className="text-sm text-positive mt-1">YES</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white">
                {Math.round((priceB || 0) * 100)}
                <span className="text-xl">%</span>
              </div>
              <div className="text-sm text-negative mt-1">NO</div>
            </div>
          </div>
          
          {/* Status badge */}
          <div className="mt-4">
            <Badge variant={status === 'live' ? 'positive' : status === 'upcoming' ? 'neutral' : 'gray'}>
              {status?.toUpperCase()}
            </Badge>
            {gameTime && (
              <div className="text-sm text-gray-400 mt-2">
                {formatLocalDateTime(gameTime, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            )}
          </div>
        </div>

        {/* Team B (Home) */}
        <div className="flex-1 text-right">
          <div className="flex items-center justify-end gap-2 mb-2 text-sm text-accent-warm">
            <House size={18} weight="duotone" />
            <span>{t('match.home')}</span>
          </div>
          <h1 className="text-lg sm:text-xl font-bold mb-2">{teamB}</h1>
          <div className="text-sm text-gray-400 mb-3">
            {t('match.rest')}: <span className="text-white">{teamBRestDays ?? '-'} {t('match.days')}</span>
          </div>
          <img
            src={getTeamLogo(teamB)}
            alt={teamB}
            className="w-16 h-16 sm:w-24 sm:h-24 object-contain ml-auto"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
        </div>
      </div>

      {/* Polymarket link */}
      {polymarketUrl && (
        <div className="mt-6 text-center border-t border-glass-border pt-4">
          <a
            href={polymarketUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-accent hover:text-accent-soft text-sm"
          >
            {t('match.viewOnPolymarket')} <FiExternalLink />
          </a>
        </div>
      )}
    </Card>
  );
}
